import gql from 'graphql-tag';
import {
	queryATSProcessingsByStatusCreatedDateIndex,
	querySFTPProcessingsByStatusCreatedDateIndex,
} from 'src/_shared/api/graphql/custom/ats-processing';
import { ListCompanyProcessingConfigs } from 'src/_shared/api/graphql/custom/company/companyProcessingConfigsGraphql.js';
import { lambda } from 'src/_shared/services/utils.js';
import { feedState } from 'src/settings-dashboard/integrations/ProcessingSettingsCard.jsx';
import { isWritesSuppressed } from './runSummary.js';

/**
 * Shared ledger fetch + health aggregation for the Admin Integrations page and
 * the Admin Console health card. One source of truth: any change to how a
 * feed's health is judged must land here, not in a component.
 */

// Legacy statuses + the dry-run lifecycle owned by the new-pipeline executor.
export const SFTP_STATUSES = [
	'completed',
	'failed',
	'pending',
	'queued', // new-pipeline live work in SQS flight (invisible to legacy pollers)
	'processing',
	'retrigger-pending', // Admin "Run now" request awaiting the executor sweep
	'dryrun-pending',
	'dryrun-processing',
	'dryrun-completed',
	'dryrun-failed',
];
// ATS gets the dryrun lifecycle too: ICIMS-Workday shadow mode pairs each
// legacy run with a new-pipeline dry run (box snapshot -> executor sweep).
// 'completed_with_errors' is written by the claus platform's executors
// (atsType CLAUS rows share this table).
export const ATS_STATUSES = [
	'completed',
	'completed_with_errors',
	'failed',
	'pending',
	'processing',
	'dryrun-pending',
	'dryrun-processing',
	'dryrun-completed',
	'dryrun-failed',
];
export const MAX_PAGES_PER_STATUS = 10; // 10 × 1000 items — well past the 100-per-integration retention.
export const STALE_HOURS = 26; // most feeds are daily; > 26h since last success is suspicious.
// 'off' = the company's integration settings say this feed shouldn't run —
// hidden by default; when shown it must not read as failing or stale.
// 'blocked' = the last run "completed" but the disable breaker suppressed all
// writes — as urgent as failed, and easy to miss because the status is green.
export const HEALTH_RANK = { failed: 0, blocked: 1, stale: 2, healthy: 3, off: 4 };

// Feed enablement is judged per (source, syncType) against the company's
// processing configs. Ledger rows the config model can't speak for stay
// visible: CLAUS feeds (scheduled by the claus platform, not Company config)
// and syncTypes outside the settings vocabulary return null = unknown.
const KNOWN_TASKS = ['employee', 'job', 'candidate', 'bonus'];

const parseCompanyConfig = (value) => {
	try {
		const parsed = typeof value === 'string' ? JSON.parse(value) : value;
		return parsed && typeof parsed === 'object' ? parsed : null;
	} catch (e) {
		return null;
	}
};

const feedEnabledFor = ({ source, companyId, syncType }, configsById) => {
	if (!configsById || source === 'CLAUS') return null;
	const task = (syncType || '').toLowerCase();
	if (!KNOWN_TASKS.includes(task)) return null;
	const configs = configsById.get(companyId);
	if (!configs) return false; // company record is gone — the feed can't be live
	// SFTP shares the settings page's definition of "on": pipelineMode live or
	// validating (dry-run). The legacy `enabled` flag alone means a retired-EC2
	// zombie config, not a running feed.
	if (source === 'SFTP') return feedState(configs.sftp?.[task]) !== 'off';
	return Boolean(configs.ats?.[task]?.enabled);
};

export const runDate = (run) => new Date(run.endDate || run.startDate || run.createdDate);
export const isDryRun = (run) => (run.status || '').startsWith('dryrun');

const callMappingConfigApi = async (variables) => {
	const res = await lambda({ endpoint: 'mapping-config-api', variables });
	const raw = res?.data ?? res;
	return typeof raw === 'string' ? JSON.parse(raw) : raw;
};

/**
 * Fast path: the mapping-config-api lambda aggregates the whole ledger
 * in-region and returns ~per-feed rows (~100KB) instead of the browser
 * downloading every run (~25MB). Returns null when the action isn't available
 * (lambda not yet deployed) or errors — callers fall back to the client-side
 * scan, so shipping the UI ahead of the lambda is safe.
 */
export const fetchIntegrationsHealthFast = async () => {
	try {
		const payload = await callMappingConfigApi({ action: 'integrationsHealth' });
		if (payload?.ok && Array.isArray(payload.integrations)) {
			return { integrations: payload.integrations, stats: payload.stats };
		}
	} catch (error) {
		console.warn('integrationsHealth fast path unavailable; falling back:', error?.message);
	}
	return null;
};

/** One feed's run history, newest first (fast-path rows carry no allRuns). */
export const fetchFeedHistory = async (row) => {
	const payload = await callMappingConfigApi({
		action: 'feedHistory',
		source: row.source,
		companyId: row.companyId,
		syncType: row.syncType,
	});
	if (!payload?.ok) throw new Error(payload?.error || 'feedHistory failed');
	return payload.runs;
};

/** Peek the pipeline DLQs: which runs are stuck, not just how many. */
export const fetchDlqMessages = async () => {
	const payload = await callMappingConfigApi({ action: 'dlqPeek' });
	if (!payload?.ok) throw new Error(payload?.error || 'dlqPeek failed');
	return payload.messages;
};

/** Re-queue one dead-letter run. If a newer file completed since, the worker's
 *  stale-replay guard skips it instead of writing. When retrying would apply an
 *  OLDER file the guard can't catch, the lambda refuses and the thrown error
 *  carries requiresForce + file details — pass {force: true} after the user
 *  explicitly confirms. */
export const redriveDlqMessage = async (entry, { force = false } = {}) => {
	const payload = await callMappingConfigApi({
		action: 'dlqRedrive',
		queueTaskType: entry.queueTaskType,
		ledgerId: entry.ledgerId,
		force,
	});
	if (!payload?.ok) {
		const error = new Error(payload?.error || 'dlqRedrive failed');
		if (payload?.requiresForce) {
			error.requiresForce = true;
			error.thisFile = payload.thisFile;
			error.latestFile = payload.latestFile;
			error.latestDate = payload.latestDate;
		}
		throw error;
	}
};

/** Remove one dead-letter message without retrying it. */
export const deleteDlqMessage = async (entry) => {
	const payload = await callMappingConfigApi({
		action: 'dlqDelete',
		queueTaskType: entry.queueTaskType,
		ledgerId: entry.ledgerId,
	});
	if (!payload?.ok) throw new Error(payload?.error || 'dlqDelete failed');
};

/** Every company's feed-enablement config, keyed by id. */
export const fetchCompanyConfigMap = async (client) => {
	const byId = new Map();
	let nextToken = null;
	do {
		const result = await client.query({
			query: ListCompanyProcessingConfigs,
			variables: { limit: 1000, nextToken },
			fetchPolicy: 'network-only',
		});
		const page = result?.data?.listCompanies;
		(page?.items || []).forEach((company) => {
			byId.set(company.id, {
				name: company.name,
				sftp: parseCompanyConfig(company.SFTPProcessingConfig),
				ats: parseCompanyConfig(company.ATSProcessingConfig),
			});
		});
		nextToken = page?.nextToken;
	} while (nextToken);
	return byId;
};

const fetchStatusPages = async (client, query, rootField, status, source, maxPages) => {
	const items = [];
	let after = null;
	for (let page = 0; page < maxPages; page++) {
		try {
			const result = await client.query({
				query: gql(query),
				variables: { status, first: 1000, after },
				fetchPolicy: 'network-only',
			});
			const response = result?.data?.[rootField];
			(response?.items || []).forEach((item) =>
				items.push({
					...item,
					// claus-platform executors write into ATSProcessingTable with
					// atsType CLAUS — surface them as their own source so they're
					// filterable and don't get ATS "Run now" semantics.
					source: item.atsType === 'CLAUS' ? 'CLAUS' : source,
				})
			);
			after = response?.nextToken;
			if (!after) break;
		} catch (error) {
			console.error(`Error fetching ${source} ${status}:`, error);
			break;
		}
	}
	return items;
};

/** Every ledger run across both tables, all statuses, tagged with `source`. */
export const fetchLedgerRuns = async (client, { maxPages = MAX_PAGES_PER_STATUS } = {}) => {
	const sftpBatches = SFTP_STATUSES.map((status) =>
		fetchStatusPages(
			client,
			querySFTPProcessingsByStatusCreatedDateIndex,
			'querySFTPProcessingsByStatusCreatedDateIndex',
			status,
			'SFTP',
			maxPages
		)
	);
	const atsBatches = ATS_STATUSES.map((status) =>
		fetchStatusPages(
			client,
			queryATSProcessingsByStatusCreatedDateIndex,
			'queryATSProcessingsByStatusCreatedDateIndex',
			status,
			'ATS',
			maxPages
		)
	);
	return (await Promise.all([...sftpBatches, ...atsBatches])).flat();
};

/** Aggregate: one row per (source, companyId, syncType[, atsType]) with health. */
export const aggregateIntegrations = (runs, configsById) => {
	// claus-platform rows carry no companyName — borrow it from any other
	// source's rows for the same company before falling back to the id.
	const nameById = new Map();
	runs.forEach((run) => {
		if (run.companyName && run.companyId) nameById.set(run.companyId, run.companyName);
	});
	const groups = new Map();
	runs.forEach((run) => {
		const key = [run.source, run.companyId, run.syncType, run.atsType || '']
			.join('|');
		if (!groups.has(key)) groups.set(key, { key, runs: [] });
		groups.get(key).runs.push(run);
	});
	return [...groups.values()].map((group) => {
		const sorted = group.runs.sort((a, b) => runDate(b) - runDate(a));
		const realRuns = sorted.filter((run) => !isDryRun(run));
		const dryRuns = sorted.filter(isDryRun);
		const latest = realRuns[0] || null;
		// A breaker-suppressed run wrote nothing — it is not a success, for
		// staleness or anything else.
		const latestCompleted = realRuns.find(
			(run) => run.status === 'completed' && !isWritesSuppressed(run)
		);
		const latestDryRun = dryRuns[0] || null;
		const active = realRuns.some((run) =>
			['pending', 'processing'].includes(run.status)
		);
		const dryRunActive = dryRuns.some((run) =>
			['dryrun-pending', 'dryrun-processing'].includes(run.status)
		);
		const staleMs = latestCompleted
			? Date.now() - runDate(latestCompleted).getTime()
			: Infinity;
		// Infer this feed's delivery cadence from the gaps between its recent
		// completed runs (median of up to 10; sub-hour gaps are same-day
		// retriggers, not deliveries). A weekly feed is not "stale" on day 2 —
		// the flat 26h rule alone cried wolf on every non-daily feed.
		const DAY_MS = 24 * 3600 * 1000;
		const completedRuns = realRuns.filter((run) => run.status === 'completed');
		const gaps = [];
		for (let i = 0; i + 1 < completedRuns.length && gaps.length < 10; i++) {
			const gap = runDate(completedRuns[i]) - runDate(completedRuns[i + 1]);
			if (gap > 3600 * 1000) gaps.push(gap);
		}
		const medianGapMs = gaps.length >= 2
			? gaps.sort((a, b) => a - b)[Math.floor(gaps.length / 2)]
			: null;
		const cadenceLabel = medianGapMs
			? medianGapMs < 1.5 * DAY_MS
				? 'daily'
				: medianGapMs < 10 * DAY_MS
				? `~every ${Math.round(medianGapMs / DAY_MS)}d`
				: 'sporadic'
			: null;
		// Stale = twice as long as this feed usually goes between files
		// (floor: the global 26h rule for feeds with no history yet).
		const staleThresholdMs = Math.max(
			STALE_HOURS * 3600 * 1000,
			medianGapMs ? 2 * medianGapMs : 0
		);
		const sample = latest || latestDryRun || sorted[0];
		const feedEnabled = feedEnabledFor(sample, configsById);
		// A disabled feed is 'off', never failing/stale — its runs are history,
		// not a problem to triage. 'blocked' outranks 'stale': the suppressed
		// run IS why the feed has no recent success.
		const health =
			feedEnabled === false
				? 'off'
				: ['failed', 'completed_with_errors'].includes(latest?.status)
				? 'failed'
				: latest?.status === 'completed' && isWritesSuppressed(latest)
				? 'blocked'
				: staleMs > staleThresholdMs
				? 'stale'
				: 'healthy';
		return {
			key: group.key,
			source: sample.source,
			companyId: sample.companyId,
			companyName:
				sample.companyName ||
				nameById.get(sample.companyId) ||
				// CLAUS-only companies never write a name into the ledger — the
				// company directory is the last resort before showing a raw id.
				configsById?.get(sample.companyId)?.name ||
				sample.companyId,
			syncType: sample.syncType,
			atsType: sample.atsType,
			feedEnabled,
			latest,
			latestCompleted,
			// Same shape as the server fast path (which sends no full row).
			latestCompletedDate: latestCompleted
				? runDate(latestCompleted).toISOString()
				: null,
			latestDryRun,
			active,
			dryRunActive,
			health,
			cadenceLabel,
			runCount: sorted.length,
			allRuns: sorted,
		};
	});
};

// Headline counts cover enabled feeds only — disabled feeds' failures are
// history, not open problems, and would otherwise cry wolf permanently.
export const healthStats = (integrations) => ({
	total: integrations.filter((row) => row.health !== 'off').length,
	off: integrations.filter((row) => row.health === 'off').length,
	failed: integrations.filter((row) => row.health === 'failed').length,
	blocked: integrations.filter((row) => row.health === 'blocked').length,
	stale: integrations.filter((row) => row.health === 'stale').length,
	running: integrations.filter((row) => row.active).length,
	dryRunning: integrations.filter((row) => row.dryRunActive).length,
});
